import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * EditMode — global per-tab "Customize Layout" toggle.
 *
 * The whole authenticated shell wraps children in <EditModeProvider>; every
 * SortableGrid + widget page then reads `useEditMode(surface).editing`.
 * State is keyed by pathname so toggling "Customize" on /app/plan does NOT
 * also flip /app/coach.
 *
 * NOTE: the default context value MUST match the Provider shape ({map,setMap})
 * so `useEditMode()` can be called safely even before a Provider mounts —
 * e.g. inside a page component that will *itself* render <DashLayout> (which
 * mounts the Provider). Without this, the destructure `{map,setMap} =
 * useContext(Ctx)` blows up on first render with "Cannot read '/app' of
 * undefined".
 */
const Ctx = createContext({ map: {}, setMap: () => {} });

export function EditModeProvider({ children }) {
  const [byPath, setByPath] = useState({});
  const value = useMemo(() => ({ map: byPath, setMap: setByPath }), [byPath]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Returns `{ editing, toggle, setEditing, key }` bound to the current
 * pathname (or to an explicit `surface` string for a shared toggle across
 * routes).
 */
export function useEditMode(surface) {
  const loc = useLocation();
  const key = surface || loc.pathname;
  const { map, setMap } = useContext(Ctx);
  const editing = !!(map && map[key]);
  const setEditing = useCallback(
    (v) => setMap((prev) => ({ ...prev, [key]: typeof v === "function" ? v(!!prev[key]) : !!v })),
    [key, setMap],
  );
  const toggle = useCallback(() => setEditing((v) => !v), [setEditing]);
  return { editing, toggle, setEditing, key };
}
